import { ZodError, z, type RefinementCtx } from 'zod';

import type {
    AccountPaymentsSuccessResponse,
    AddPaymentErrorResponse,
} from '@libs/common-next';
import { getNextApiRoute, typedFetch } from '@libs/common-next';
import { getLocale, LocaleShorthand, isLocaleCA } from '@libs/core';
import {
    expirationDateWrongLengthMsg,
    expirationDateOldErrorMsg,
    expirationDateFutureErrorMsg,
} from './validationErrors';
import { MAX_ADYEN_EXPIRATION_YEAR } from './constants';
import type { CardState } from '../../hooks/types';
import type {
    AddPaymentFormInterface,
    EditPaymentFormInterface,
    PaymentFormWithAddressObject,
} from './schema';
import type { AddressFormType } from '../AddressForm/Address.schema';
import { addressFormSchema } from '../AddressForm/Address.schema';

export const validateExpirationDate = (
    inputValue: string,
    context: RefinementCtx
) => {
    const [month, year] = inputValue.split('/').map(Number);
    const fullYear = Number(`20${year}`);
    const today = new Date();
    const currentYear = today.getFullYear() % 100;
    const expDateObj = new Date(fullYear, month - 1);

    if (inputValue.length !== 5) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: expirationDateWrongLengthMsg,
            path: ['expirationDate'],
        });
    }

    if (expDateObj < today) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: expirationDateOldErrorMsg,
            path: ['expirationDate'],
        });
    }

    if (year > currentYear + MAX_ADYEN_EXPIRATION_YEAR) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: expirationDateFutureErrorMsg,
            path: ['expirationDate'],
        });
    }
};

export const handleSetExpirationDate = (
    event: React.ChangeEvent<Element>,
    callback: (field: 'expirationDate', value: string | undefined) => void
) => {
    const { value } = event.target as HTMLInputElement;
    let santized = value.replace(/\D/g, '');

    if (santized.length > 4) {
        santized = santized.slice(0, 4);
    }

    const formattedInput = santized.replace(
        /^(\d{0,2})(\d{0,2})/,
        (_, month, year) => {
            if (month.length === 1 && parseInt(month, 10) > 1) {
                month = `0${month}`;
            }

            if (parseInt(month, 10) > 12) {
                month = '12';
            }

            return month + (year ? `/${year}` : '');
        }
    );

    callback('expirationDate', formattedInput);
};

const getAddAddressRequestBody = (
    paymentForm: PaymentFormWithAddressObject
) => {
    const { address, firstName, lastName } = paymentForm;

    return {
        firstName,
        lastName,
        countryAbbreviation: isLocaleCA() ? 'CA' : 'US',
        stateProvinceAbbreviation: address.state,
        zipPostalCode: address.zipCode,
        city: address.city,
        streetLine1: address.streetAddress,
        streetLine2: address.apt,
        isActive: true,
        isPrimary: false,
        phoneNumber: paymentForm.phone,
        addressType: 'other',
    };
};

export const addPayment = async (
    adyenFormState: CardState,
    paymentForm: AddPaymentFormInterface
) => {
    const fullName = adyenFormState.data.paymentMethod.holderName;
    const requestBody = {
        paymentDetails: {
            encryptedCardNumber:
                adyenFormState?.data.paymentMethod.encryptedCardNumber,
            encryptedExpiryMonth:
                adyenFormState?.data.paymentMethod.encryptedExpiryMonth,
            encryptedExpiryYear:
                adyenFormState?.data.paymentMethod.encryptedExpiryYear,
            encryptedSecurityCode:
                adyenFormState?.data.paymentMethod.encryptedSecurityCode,
        },
        countryCode: LocaleShorthand[getLocale()],
        fullName,
        isPrimary: paymentForm.isPrimary,
    };

    return await typedFetch<
        AccountPaymentsSuccessResponse | AddPaymentErrorResponse
    >(getNextApiRoute(`account/payments`), {
        method: 'POST',
        body: JSON.stringify(
            paymentForm.addressType === 'form'
                ? {
                      ...requestBody,
                      address: getAddAddressRequestBody(paymentForm),
                  }
                : {
                      ...requestBody,
                      addressId: Number(paymentForm?.selectedAddressId),
                  }
        ),
        additionalSuccessCodes: [400],
    });
};

export const updatePayment = async (paymentForm: EditPaymentFormInterface) => {
    const [expiryMonth, expiryYear] = paymentForm.expirationDate.split('/');
    const requestBody = {
        countryCode: LocaleShorthand[getLocale()],
        paymentDetails: {
            storedPaymentMethodId: paymentForm.ccToken,
            expiryMonth,
            expiryYear: `20${expiryYear}`,
        },
        isPrimary: paymentForm.isPrimary,
        fullName: paymentForm.nameOnCard,
    };
    return await typedFetch<AccountPaymentsSuccessResponse>(
        getNextApiRoute(`account/payments/${paymentForm.cardId}`),
        {
            method: 'PUT',
            body: JSON.stringify(
                paymentForm.addressType === 'form'
                    ? {
                          ...requestBody,
                          address: getAddAddressRequestBody(paymentForm),
                      }
                    : {
                          ...requestBody,
                          addressId: Number(paymentForm?.selectedAddressId),
                      }
            ),
            additionalSuccessCodes: [400],
        }
    );
};

export const formatCreditCardExpirationDate = (expirationDate: string) => {
    const characters = [...expirationDate];
    characters.splice(2, 0, '/');

    return characters.join('');
};

export const validateAddressForm = async (values: AddressFormType) => {
    try {
        addressFormSchema.parse(values);
    } catch (error) {
        if (error instanceof ZodError) {
            return error.formErrors.fieldErrors;
        }
    }

    return null;
};
